// Overview tab — match summary at a glance: turn counter, year, victory
// progress, doctrine breakdown, and recent event feed. Read-only; no
// callbacks. Pure projection of sim state passed in via props.

import { useT } from '../../i18n/index.js';
import { ARCHETYPES } from '../DoctrinePanel/archetypes.js';
import { type FormattableEvent, eventLabel, formatPayload } from './formatEventPayload.js';
import type { DoctrineState, VictoryProgress } from './types.js';

interface OverviewTabProps {
  readonly tick: bigint;
  readonly events: readonly FormattableEvent[];
  readonly victoryProgress: VictoryProgress | null;
  readonly localPlayer: DoctrineState | null;
}

const RECENT_EVENT_LIMIT = 8;

export function OverviewTab({ tick, events, victoryProgress, localPlayer }: OverviewTabProps) {
  const t = useT();
  // Match-summary cadence is separate from the continuous day/year calendar.
  const turn = Math.floor(Number(tick) / 6) + 1;
  const year = Math.floor((turn - 1) / 12) + 1;
  const headerLabel = t('overview.turnYear', { turn, year });
  const recent = events.slice(-RECENT_EVENT_LIMIT);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between">
        <span className="text-xs font-medium text-foreground">{headerLabel}</span>
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="text-[11px] font-semibold tracking-[0.18em] text-muted-foreground">
          {t('overview.victoryProgress')}
        </span>
        {victoryProgress === null ? (
          <p className="text-[10px] text-muted-foreground">{t('overview.tracking')}</p>
        ) : (
          <div className="flex flex-col gap-1">
            <ProgressRow label={t('overview.wonder')} pct={victoryProgress.wonderPct} />
            <ProgressRow label={t('overview.domination')} pct={victoryProgress.dominationPct} />
            <ProgressRow label={t('victory.path.faith')} pct={victoryProgress.faithPct} />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="text-[11px] font-semibold tracking-[0.18em] text-muted-foreground">
          {t('overview.doctrine')}
        </span>
        {localPlayer === null ? (
          <p className="text-[10px] text-muted-foreground">{t('overview.doctrineEmpty')}</p>
        ) : (
          <div className="flex flex-col gap-0.5">
            {ARCHETYPES.map((archetype, i) => (
              <div
                key={archetype.id}
                className="flex items-center justify-between text-[11px] tabular-nums"
              >
                <span className="text-muted-foreground">{t(`archetype.${archetype.id}`)}</span>
                <span className="font-medium text-foreground">{localPlayer.spent[i] ?? 0}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="text-[11px] font-semibold tracking-[0.18em] text-muted-foreground">
          {t('overview.recentEvents')}
        </span>
        {recent.length === 0 ? (
          <p className="text-[10px] text-muted-foreground">{t('overview.noEvents')}</p>
        ) : (
          <div className="flex flex-col gap-1">
            {recent.map((event) => {
              const label = eventLabel(t, event.type, event.payload);
              const detail = formatPayload(t, event.type, event.payload);
              return (
                <div
                  key={event.id}
                  className="rounded bg-muted/60 px-2 py-1 text-[10px] leading-relaxed text-foreground"
                >
                  <span className="font-medium">{label}</span>
                  {detail ? <span className="ml-1.5 text-muted-foreground">{detail}</span> : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

interface ProgressRowProps {
  readonly label: string;
  readonly pct: number;
}

function ProgressRow({ label, pct }: ProgressRowProps) {
  return (
    <div className="flex items-center justify-between gap-2 text-[11px]">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono tabular-nums text-foreground">{`${pct}%`}</span>
    </div>
  );
}
